(() => {
  'use strict';
  if (window.elyManufacturingPage) {
    window.elyManufacturingPage.init(document);
    return;
  }
  const controllers = new Map();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const unlock = () => document.documentElement.classList.toggle('mfg-dialog-open', Boolean(document.querySelector('dialog[data-mfg-dialog][open]')));

  function init(scope) {
    const roots = [...scope.querySelectorAll('[data-mfg-dialogs], [data-mfg-finishing]')];
    if (scope.matches?.('[data-mfg-dialogs], [data-mfg-finishing]')) roots.unshift(scope);
    roots.forEach(root => {
      if (controllers.has(root)) return;
      const controller = new AbortController();
      const { signal } = controller;
      controllers.set(root, controller);

      if (root.matches('[data-mfg-dialogs]')) {
        root.addEventListener('click', event => {
          const opener = event.target.closest('[data-mfg-open]');
          const closer = event.target.closest('[data-mfg-close]');
          if (opener) {
            const dialog = root.querySelector(`#${CSS.escape(opener.getAttribute('aria-controls'))}`);
            if (!dialog) return;
            dialog.showModal();
            unlock();
          } else if (closer) {
            closer.closest('dialog').close();
          }
        }, { signal });
        root.querySelectorAll('dialog').forEach(dialog => {
          dialog.addEventListener('close', unlock, { signal });
          dialog.addEventListener('click', event => {
            if (event.target !== dialog) return;
            const box = dialog.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
          }, { signal });
        });
      }

      if (root.matches('[data-mfg-capabilities]')) {
        const track = root.querySelector('[data-mfg-track]');
        const cards = [...track.querySelectorAll('.mfg-card')];
        const previous = root.querySelector('[data-mfg-prev]');
        const next = root.querySelector('[data-mfg-next]');
        const stride = () => cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
        const moveTo = left => track.scrollTo({ left, behavior:reducedMotion() ? 'instant' : 'smooth' });
        let scheduled = 0;
        function updateCarousel() {
          scheduled = 0;
          const max = track.scrollWidth - track.clientWidth;
          previous.disabled = track.scrollLeft < 2;
          next.disabled = track.scrollLeft >= max - 2;
        }
        previous.addEventListener('click', () => moveTo(Math.max(0,track.scrollLeft-stride())), { signal });
        next.addEventListener('click', () => moveTo(Math.min(track.scrollWidth-track.clientWidth,track.scrollLeft+stride())), { signal });
        track.addEventListener('scroll', () => {
          if (!scheduled) scheduled = requestAnimationFrame(updateCarousel);
        }, { signal, passive:true });
        track.addEventListener('keydown', event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const current = cards.indexOf(document.activeElement);
          if (current >= 0) {
            const index = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : Math.max(0,Math.min(cards.length - 1,current + (event.key === 'ArrowRight' ? 1 : -1)));
            cards[index].focus({ preventScroll:true });
            moveTo(index*stride());
          } else {
            moveTo(event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth : track.scrollLeft + stride()*(event.key === 'ArrowRight' ? 1 : -1));
          }
        }, { signal });
        const observer = new ResizeObserver(updateCarousel);
        observer.observe(track);
        signal.addEventListener('abort', () => { observer.disconnect(); cancelAnimationFrame(scheduled); }, { once:true });
        updateCarousel();
      }

      if (root.matches('[data-mfg-finishing]')) {
        const entries = [...root.querySelectorAll('[data-mfg-finish]')];
        const frame = root.querySelector('[data-mfg-finish-frame]');
        let desktopImage = frame.querySelector('img');
        const prepared = new Map();
        let revision = 0;
        // Hidden, lazy images do not download until their details panel opens.
        // Prepare decoded image elements near the section, then reuse them on desktop.
        function prepare(entry) {
          if (prepared.has(entry)) return prepared.get(entry);
          const source = entry.querySelector('[data-mfg-finish-image]');
          if (!source) return Promise.resolve(null);
          const image = source.cloneNode(false);
          image.removeAttribute('data-mfg-finish-image');
          image.loading = 'eager';
          image.fetchPriority = 'low';
          source.loading = 'eager';
          const ready = image.decode().then(() => image).catch(() => {
            prepared.delete(entry); // Allow a later interaction to retry a failed request.
            return null;
          });
          prepared.set(entry, ready);
          return ready;
        }
        function update(entry) {
          const request = ++revision;
          const source = entry?.querySelector('[data-mfg-finish-image]');
          root.dataset.hasMedia = source ? 'true' : 'false';
          if (!source) {
            frame.hidden = true;
            frame.removeAttribute('aria-busy');
            return;
          }
          frame.setAttribute('aria-busy', 'true');
          prepare(entry).then(image => {
            if (signal.aborted || request !== revision || !entry.open) return;
            frame.removeAttribute('aria-busy');
            // Keep the previous image until the next image is ready.
            if (!image) return;
            if (image !== desktopImage) desktopImage.replaceWith(image);
            desktopImage = image;
            frame.hidden = false;
          });
        }
        const warm = () => entries.forEach(entry => prepare(entry));
        entries.forEach(entry => {
          const summary = entry.querySelector('summary');
          summary.addEventListener('pointerenter', () => prepare(entry), { signal });
          summary.addEventListener('focus', () => prepare(entry), { signal });
          entry.addEventListener('toggle', () => {
            if (entry.open) {
              entries.forEach(other => { if (other !== entry) other.open = false; });
              update(entry);
            } else if (!entries.some(other => other.open)) {
              ++revision;
              frame.removeAttribute('aria-busy');
            }
          }, { signal });
        });
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(changes => {
            if (changes.some(change => change.isIntersecting)) {
              warm();
              observer.disconnect();
            }
          }, { rootMargin:'1000px 0px' });
          observer.observe(root);
          signal.addEventListener('abort', () => observer.disconnect(), { once:true });
        } else {
          warm();
        }
        signal.addEventListener('abort', () => { ++revision; prepared.clear(); }, { once:true });
      }
    });

    // Send generic quote entries directly to the existing quote form.
    // Advice links and process-specific links keep their own destinations.
    if (document.querySelector('[data-mfg-page]')) {
      document.querySelectorAll('a[href]').forEach(link => {
        const target = new URL(link.getAttribute('href'), location.href);
        if (link.hasAttribute('data-mfg-outbound-quote') || target.origin !== location.origin || target.pathname !== '/pages/request-a-quote') return;
        if ([...target.searchParams.keys()].some(key => key !== 'intent')) return;
        if (target.searchParams.has('intent') && target.searchParams.get('intent') !== 'quote') return;
        if (!/^\s*(get a quote|submit drawings)\s*$/i.test(link.textContent)) return;
        target.searchParams.set('intent', 'quote');
        link.href = target.href;
        link.textContent = 'Get a Quote';
      });
    }
  }

  window.elyManufacturingPage = { init };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, root) => {
      if (event.target.contains(root)) {
        controller.abort();
        root.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        controllers.delete(root);
      }
    });
    unlock();
  });
})();
